  import { useState, useRef, useEffect, useCallback } from "react";
    import useWindowSize from "../hooks/useWindowSize";
    import apiService from "../services/api";
    import { validateDateOfBirth, validateCertificationDates } from "../utils/dateValidation";

/* ═══════════════════════ CONSTANTS ═══════════════════════ */
const W = "#ffffff";
const CARD_BG = "#042558";
const INPUT_BG = "rgba(255,255,255,0.08)";
const BORDER = "rgba(255,255,255,0.3)";
const ACCENT = "#4A90E2";
const GREEN = "#6ADD00";

const ROLE_OPTIONS = ["RN", "LPN", "CNA", "PT", "NP", "ADMIN"];
const SHIFT_OPTIONS = ["DAY", "EVENING", "NIGHT"];
const STATUS_OPTIONS = ["ACTIVE", "INACTIVE", "ON_LEAVE"];
const CERT_TYPE_OPTIONS = [
  "BLS",
  "ACLS",
  "CPI",
  "FALL_PREVENTION",
  "DEMENTIA_CARE",
  "RN_LICENSE",
  "LPN_LICENSE",
  "CNA_LICENSE",
  "PT_LICENSE",
  "NP_LICENSE",
  "OTHER"
];

/* ═══════════════════════ HELPER COMPONENTS ═══════════════════════ */
const DetailItem = ({ label, value, valueColor = "#ffffff" }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "rgba(255,255,255,0.6)",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: valueColor,
        }}
      >
        {value}
      </span>
    </div>
  );
};

/* ═══════════════════════ STYLED INPUT COMPONENT ═══════════════════════ */
function FormField({ label, children, required, isMobile }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label
        style={{
          fontSize: isMobile ? 12 : 14,
          fontWeight: 600,
          color: W,
          opacity: 0.85,
        }}
      >
        {label} {required && <span style={{ color: "#FF6B6B" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text", ...rest }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        background: INPUT_BG,
        border: `1.5px solid ${BORDER}`,
        borderRadius: 10,
        padding: "12px 16px",
        fontSize: 14,
        color: W,
        fontFamily: "'Segoe UI', sans-serif",
        outline: "none",
        transition: "border-color 0.2s",
        width: "100%",
        boxSizing: "border-box",
      }}
      onFocus={(e) => (e.target.style.borderColor = ACCENT)}
      onBlur={(e) => (e.target.style.borderColor = BORDER)}
      {...rest}
    />
  );
}

function SelectInput({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: INPUT_BG,
        border: `1.5px solid ${BORDER}`,
        borderRadius: 10,
        padding: "12px 16px",
        fontSize: 14,
        color: value ? W : "rgba(255,255,255,0.4)",
        fontFamily: "'Segoe UI', sans-serif",
        outline: "none",
        cursor: "pointer",
        width: "100%",
        boxSizing: "border-box",
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='white' stroke-width='2' fill='none'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 16px center",
      }}
    >
      <option value="" style={{ background: "#0e2240", color: W }}>
        {placeholder || "Select..."}
      </option>
      {options.map((opt) => (
        <option key={opt} value={opt} style={{ background: "#0e2240", color: W }}>
          {opt}
        </option>
      ))}
    </select>
  );
}

/* ═══════════════════════ PHOTO UPLOAD COMPONENT ═══════════════════════ */
function PhotoUpload({ photoPreview, onPhotoSelect, isMobile }) {
  const fileInputRef = useRef(null);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) onPhotoSelect(file);
  }, [onPhotoSelect]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 16,
      }}
    >
      {/* Photo Preview Circle */}
      <div
        onClick={handleClick}
        style={{
          width: isMobile ? 120 : 150,
          height: isMobile ? 120 : 150,
          borderRadius: "50%",
          border: `3px dashed ${photoPreview ? GREEN : BORDER}`,
          background: photoPreview
            ? "transparent"
            : "rgba(255,255,255,0.05)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          overflow: "hidden",
          transition: "all 0.3s",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = ACCENT;
          e.currentTarget.style.transform = "scale(1.03)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = photoPreview ? GREEN : BORDER;
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        {photoPreview ? (
          <img
            src={photoPreview}
            alt="Preview"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "50%",
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke={W}
              strokeWidth="1.5"
              strokeLinecap="round"
              style={{ opacity: 0.5 }}
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            <span
              style={{
                fontSize: 11,
                color: W,
                opacity: 0.5,
                textAlign: "center",
              }}
            >
              Click to upload
            </span>
          </div>
        )}

        {/* Hover overlay when photo exists */}
        {photoPreview && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke={W}
              strokeWidth="2"
            >
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <span
        style={{
          fontSize: 12,
          color: W,
          opacity: 0.5,
          textAlign: "center",
        }}
      >
        JPG, PNG or WebP (max 5MB)
      </span>
    </div>
  );
}

/* ═══════════════════════ MAIN PAGE ═══════════════════════ */
export default function AddCaregiverPage({ onBack, onCaregiverAdded, user }) {
  const { isMobile } = useWindowSize();

  // Get facility name from user session
  const facilityName = user?.facilityName || "Facility";

  // Existing caregivers for duplicate checking
  const [existingCaregivers, setExistingCaregivers] = useState([]);
  
  // Load existing caregivers on mount for duplicate checking
  useEffect(() => {
    const loadCaregivers = async () => {
      try {
        const data = await apiService.getAllCaregivers();
        setExistingCaregivers(data.caregivers || []);
      } catch (err) {
        console.error('Failed to load caregivers for validation:', err);
        // Don't block the form if we can't load caregivers
      }
    };
    loadCaregivers();
  }, []);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [primaryShift, setPrimaryShift] = useState("");
  const [badgeId, setBadgeId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
    const [status, setStatus] = useState("ACTIVE");
  const [fallResponseTrained, setFallResponseTrained] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(true);
    const [maxResidentLoad, setMaxResidentLoad] = useState("");
  const [isAvailable, setIsAvailable] = useState(true);

    // Sync isAvailable with status changes
  useEffect(() => {
    if (status === "ACTIVE") {
      setIsAvailable(true);
    } else if (status === "INACTIVE" || status === "ON_LEAVE") {
      setIsAvailable(false);
    }
  }, [status]);

  // Certifications state
  const [certifications, setCertifications] = useState([]);
  const [showAddCert, setShowAddCert] = useState(false);
  const [newCert, setNewCert] = useState({
    cert_type: "",
    cert_name: "",
    cert_number: "",
    issuing_body: "",
    issued_date: "",
    expiry_date: "",
  });

  // Photo state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

    // Submission state
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [createdCaregiver, setCreatedCaregiver] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [showSuccessCard, setShowSuccessCard] = useState(false);

  const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
  const ALLOWED_PHOTO_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];

  const handlePhotoSelect = (file) => {
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_PHOTO_TYPES.includes(file.type) || !ALLOWED_PHOTO_EXTENSIONS.includes(ext)) {
      setError("Invalid file type. Only JPG, PNG, and WebP images are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Photo must be less than 5MB");
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
    setError(null);
  };

      // Phone number validation helper
  const validatePhoneNumber = (phone) => {
    if (!phone || phone.trim() === "") return true; // Optional field
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

    const validateForm = () => {
    if (!firstName.trim()) return "First name is required";
    if (!lastName.trim()) return "Last name is required";
    if (!dob.trim()) return "Date of birth is required";
    
    // Validate DOB with comprehensive calendar and business logic validation
    const dobValidation = validateDateOfBirth(dob, 18); // Minimum age 18 for caregivers
    if (!dobValidation.isValid) {
      return dobValidation.error;
    }
    
    if (!email.trim()) return "Email is required";
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Valid email is required";
    
    // Phone validation if provided
    if (phone.trim() && !validatePhoneNumber(phone)) {
      return "Invalid phone number. Please use format: (555) 123-4567 or 5551234567";
    }
    
    if (!role) return "Role is required";
    if (!primaryShift) return "Primary shift is required";
    if (!photoFile) return "Photo is required for caregiver identification";
    
    // Check for duplicate email
    const trimmedEmail = email.trim().toLowerCase();
    const duplicateEmail = existingCaregivers.find(caregiver => {
      const existingEmail = (caregiver.email || '').trim().toLowerCase();
      return existingEmail === trimmedEmail;
    });
    
    if (duplicateEmail) {
      const caregiverName = `${duplicateEmail.first_name || ''} ${duplicateEmail.last_name || ''}`.trim() || 'another caregiver';
      return `Email ${email.trim()} is already assigned to ${caregiverName}. Please use a different email.`;
    }
    
    // Check for duplicate badge ID (only if provided)
    if (badgeId.trim()) {
      const trimmedBadgeId = badgeId.trim().toLowerCase();
      const duplicateBadge = existingCaregivers.find(caregiver => {
        const existingBadge = (caregiver.badge_id || '').trim().toLowerCase();
        return existingBadge && existingBadge === trimmedBadgeId;
      });
      
      if (duplicateBadge) {
        const caregiverName = `${duplicateBadge.first_name || ''} ${duplicateBadge.last_name || ''}`.trim() || 'another caregiver';
        return `Badge ID ${badgeId.trim()} is already assigned to ${caregiverName}. Please use a different badge ID or leave it blank for auto-generation.`;
      }
    }
    
    // Check for duplicate employee ID (only if provided)
    if (employeeId.trim()) {
      const trimmedEmployeeId = employeeId.trim().toLowerCase();
      const duplicateEmployee = existingCaregivers.find(caregiver => {
        const existingEmployee = (caregiver.employee_id || '').trim().toLowerCase();
        return existingEmployee && existingEmployee === trimmedEmployeeId;
      });
      
      if (duplicateEmployee) {
        const caregiverName = `${duplicateEmployee.first_name || ''} ${duplicateEmployee.last_name || ''}`.trim() || 'another caregiver';
        return `Employee ID ${employeeId.trim()} is already assigned to ${caregiverName}. Please use a different employee ID or leave it blank for auto-generation.`;
      }
    }
    
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);

        try {
                        const caregiverData = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        dob: dob.trim(),
        email: email.trim(),
        phone: phone.trim() || "N/A",
        role: role,
        primary_shift: primaryShift,
        badge_id: badgeId.trim() || "",
        employee_id: employeeId.trim() || "",
        status: status,
        fall_response_trained: fallResponseTrained,
        mfa_enabled: mfaEnabled,
        max_resident_load: maxResidentLoad ? parseInt(maxResidentLoad) : 10,
                is_available: isAvailable,
        certifications: certifications,
      };

      const result = await apiService.createCaregiver(caregiverData, photoFile);
      
            setCreatedCaregiver(result.caregiver);
            setShowPreview(true);
    } catch (err) {
      console.error("Failed to create caregiver:", err);
      setError(err.message || "Failed to create caregiver. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

                const handleConfirm = () => {
      setShowPreview(false);
      setShowSuccessCard(true);
    };

    const handleSuccessOk = () => {
      if (onCaregiverAdded && createdCaregiver) {
        onCaregiverAdded(createdCaregiver);
      }
      // Navigate back to list
      if (onBack) {
        onBack();
      }
    };

  

  const handleCancelAndEdit = async () => {
    // Delete the created caregiver
    if (createdCaregiver && createdCaregiver.caregiver_id) {
      try {
        await apiService.deleteCaregiver(createdCaregiver.caregiver_id);
      } catch (err) {
        console.error('Failed to delete caregiver:', err);
      }
    }
        // Reset to form view with data preserved
    setShowPreview(false);
    setCreatedCaregiver(null);
  };

  const gridCols = isMobile ? "1fr" : "1fr 1fr";

  return (
    <main
      style={{
        flex: 1,
        padding: isMobile ? "10px 10px" : "12px 16px",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minWidth: 0,
        fontFamily: "'Segoe UI', sans-serif",
        color: W,
        position: "relative",
        zIndex: 1,
        paddingTop: isMobile ? 75 : 82,
        paddingBottom: isMobile ? 10 : 30,
        marginTop: 0,
        minHeight: "100vh",
        boxSizing: "border-box",
      }}
    >
      {/* ═══ HEADER ═══ */}
      <div
        style={{
          background: CARD_BG,
          border: "1px solid #FFFFFF",
          borderRadius: 20,
          padding: isMobile ? "16px" : "20px 28px",
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        {/* Back Button */}
        <button
          onClick={onBack}
          style={{
            background: "rgba(255,255,255,0.1)",
            border: "1.5px solid rgba(255,255,255,0.3)",
            borderRadius: 12,
            padding: "10px 14px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: W,
            fontSize: 14,
            fontWeight: 600,
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.18)";
            e.currentTarget.style.transform = "translateX(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.1)";
            e.currentTarget.style.transform = "translateX(0)";
          }}
        >
                    <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={W}
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Back to Caregivers
        </button>

        <h1
          style={{
            margin: 0,
            fontSize: isMobile ? 20 : 26,
            fontWeight: 700,
            flex: 1,
          }}
        >
          Add New Caregiver
        </h1>

                {/* Facility Badge */}
        <div
          style={{
            background: "rgba(74,144,226,0.2)",
            border: "1px solid rgba(74,144,226,0.4)",
            borderRadius: 8,
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 600,
            color: ACCENT,
            whiteSpace: "nowrap",
          }}
        >
          {facilityName}
        </div>
            </div>

                                    {/* ═══ SUCCESS CARD ═══ */}
      {showSuccessCard && createdCaregiver && (
        <div
          style={{
            background: CARD_BG,
            border: "1px solid #FFFFFF",
            borderRadius: 20,
            padding: isMobile ? "30px 20px" : "40px 30px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 24,
            textAlign: "center",
            animation: "fadeIn 0.3s ease-out",
          }}
        >
          {/* Success Icon */}
          <div
            style={{
              width: isMobile ? 80 : 100,
              height: isMobile ? 80 : 100,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #6ADD00, #4CAF50)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(106,221,0,0.4)",
              animation: "scaleIn 0.4s ease-out",
            }}
          >
            <svg
              width={isMobile ? "48" : "56"}
              height={isMobile ? "48" : "56"}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>

          {/* Success Message */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <h2
              style={{
                margin: 0,
                fontSize: isMobile ? 24 : 32,
                fontWeight: 700,
                color: GREEN,
              }}
            >
              Caregiver Added Successfully!
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: isMobile ? 14 : 16,
                color: W,
                opacity: 0.8,
                lineHeight: 1.6,
              }}
            >
              <strong>{firstName} {lastName}</strong> has been successfully added to the system.
              <br />
              The caregiver profile is now active and ready for assignments.
            </p>
          </div>

          {/* Caregiver Info Summary */}
          <div
            style={{
              background: "rgba(106,221,0,0.1)",
              border: "1.5px solid rgba(106,221,0,0.3)",
              borderRadius: 12,
              padding: "16px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              width: "100%",
              maxWidth: 400,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
              <span style={{ color: W, opacity: 0.7 }}>Role:</span>
              <span style={{ color: W, fontWeight: 600 }}>{role}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
              <span style={{ color: W, opacity: 0.7 }}>Primary Shift:</span>
              <span style={{ color: W, fontWeight: 600 }}>{primaryShift}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
              <span style={{ color: W, opacity: 0.7 }}>Status:</span>
              <span style={{ color: W, fontWeight: 600 }}>{status}</span>
            </div>
            {(badgeId || createdCaregiver.badge_id) && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                <span style={{ color: W, opacity: 0.7 }}>Badge ID:</span>
                <span style={{ color: W, fontWeight: 600 }}>{badgeId || createdCaregiver.badge_id}</span>
              </div>
            )}
          </div>

          {/* OK Button */}
          <button
            type="button"
            onClick={handleSuccessOk}
            style={{
              background: "linear-gradient(135deg, #6ADD00, #4CAF50)",
              border: "none",
              borderRadius: 12,
              padding: isMobile ? "14px 50px" : "16px 60px",
              fontSize: isMobile ? 16 : 18,
              fontWeight: 700,
              color: W,
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow: "0 4px 16px rgba(106,221,0,0.3)",
              marginTop: 8,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(106,221,0,0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 16px rgba(106,221,0,0.3)";
            }}
          >
            OK
          </button>
        </div>
      )}

                        {/* ═══ PREVIEW/CONFIRMATION PAGE ═══ */}
            {showPreview && createdCaregiver && !showSuccessCard && (
        <div
          style={{
            background: CARD_BG,
            border: "1px solid #FFFFFF",
            borderRadius: 20,
            padding: isMobile ? "20px" : "30px",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {/* Preview Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              paddingBottom: 20,
              borderBottom: `2px solid ${GREEN}`,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: GREEN,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ffffff"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: isMobile ? 20 : 24,
                  fontWeight: 700,
                  color: W,
                }}
              >
                Review Caregiver Details
              </h2>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: 14,
                  color: W,
                  opacity: 0.7,
                }}
              >
                Please review the information below and confirm to save
              </p>
            </div>
          </div>

          {/* Photo and Basic Info */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 24,
              alignItems: isMobile ? "center" : "flex-start",
            }}
          >
            {/* Photo */}
            {photoPreview && (
              <div
                style={{
                  width: isMobile ? 120 : 150,
                  height: isMobile ? 120 : 150,
                  borderRadius: "50%",
                  border: `3px solid ${GREEN}`,
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <img
                  src={photoPreview}
                  alt="Caregiver"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            )}

            {/* Name and ID */}
            <div style={{ flex: 1 }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: isMobile ? 22 : 28,
                  fontWeight: 700,
                  color: W,
                }}
              >
                {firstName} {lastName}
              </h3>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  marginTop: 12,
                }}
              >
                <div
                  style={{
                    background: "rgba(74,144,226,0.2)",
                    border: "1px solid rgba(74,144,226,0.4)",
                    borderRadius: 8,
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: ACCENT,
                  }}
                >
                  {role}
                </div>
                <div
                  style={{
                    background: "rgba(106,221,0,0.2)",
                    border: "1px solid rgba(106,221,0,0.4)",
                    borderRadius: 8,
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: GREEN,
                  }}
                >
                  {status}
                </div>
                <div
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    borderRadius: 8,
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: W,
                  }}
                >
                  {primaryShift} Shift
                </div>
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              gap: 20,
              padding: "20px 0",
              borderTop: `1.5px solid ${BORDER}`,
            }}
          >
            <DetailItem label="Email" value={email} />
            <DetailItem label="Phone" value={phone || "N/A"} />
            <DetailItem label="Date of Birth" value={dob} />
            <DetailItem label="Badge ID" value={badgeId || createdCaregiver.badge_id || "Auto-generated"} />
            <DetailItem label="Employee ID" value={employeeId || createdCaregiver.employee_id || "Auto-generated"} />
            <DetailItem label="Max Resident Load" value={maxResidentLoad || "10"} />
            <DetailItem
              label="Fall Response Trained"
              value={fallResponseTrained ? "Yes" : "No"}
              valueColor={fallResponseTrained ? GREEN : "rgba(255,255,255,0.5)"}
            />
            <DetailItem
              label="MFA Enabled"
              value={mfaEnabled ? "Yes" : "No"}
              valueColor={mfaEnabled ? GREEN : "rgba(255,255,255,0.5)"}
            />
            <DetailItem
              label="Available for Assignment"
              value={isAvailable ? "Yes" : "No"}
              valueColor={isAvailable ? GREEN : "rgba(255,255,255,0.5)"}
            />
          </div>

          {/* Certifications */}
          {certifications.length > 0 && (
            <div
              style={{
                padding: "20px 0",
                borderTop: `1.5px solid ${BORDER}`,
              }}
            >
              <h4
                style={{
                  margin: "0 0 12px",
                  fontSize: 16,
                  fontWeight: 700,
                  color: ACCENT,
                }}
              >
                Certifications ({certifications.length})
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {certifications.map((cert, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      border: `1px solid ${BORDER}`,
                      borderRadius: 8,
                      padding: "10px 14px",
                      fontSize: 13,
                      color: W,
                    }}
                  >
                    <strong>{cert.cert_name || cert.cert_type}</strong>
                    {cert.cert_number && ` • #${cert.cert_number}`}
                    {cert.expiry_date && ` • Expires: ${cert.expiry_date}`}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 16,
              paddingTop: 20,
              borderTop: `1.5px solid ${BORDER}`,
            }}
          >
            <button
              type="button"
              onClick={handleCancelAndEdit}
              style={{
                background: "rgba(255,107,107,0.15)",
                border: "1.5px solid rgba(255,107,107,0.5)",
                borderRadius: 12,
                padding: "14px 32px",
                fontSize: 15,
                fontWeight: 600,
                color: "#FF6B6B",
                cursor: "pointer",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,107,107,0.25)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,107,107,0.15)";
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FF6B6B"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
                            Cancel & Make Changes
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              style={{
                background: "linear-gradient(135deg, #6ADD00, #4CAF50)",
                border: "none",
                borderRadius: 12,
                padding: "14px 40px",
                fontSize: 15,
                fontWeight: 700,
                color: W,
                cursor: "pointer",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 10,
                boxShadow: "0 4px 16px rgba(106,221,0,0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={W}
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Confirm & Save
            </button>
          </div>
        </div>
      )}

                                                {/* ═══ FORM BODY ═══ */}
      {!showPreview && !showSuccessCard && (
        <div
          style={{
            background: CARD_BG,
            border: "1px solid #FFFFFF",
            borderRadius: 20,
            padding: isMobile ? "20px" : "30px",
            display: "flex",
            flexDirection: "column",
            gap: 28,
          }}
        >
          {/* ── Photo Upload Section ── */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "center" : "flex-start",
              gap: isMobile ? 20 : 40,
              paddingBottom: 24,
              borderBottom: `1.5px solid ${BORDER}`,
            }}
          >
            <PhotoUpload
              photoPreview={photoPreview}
              onPhotoSelect={handlePhotoSelect}
              isMobile={isMobile}
            />
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 700,
                  color: W,
                }}
              >
                Caregiver Photo{" "}
                <span style={{ color: "#FF6B6B", fontSize: 14 }}>
                  * Required
                </span>
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: W,
                  opacity: 0.6,
                  lineHeight: 1.5,
                }}
              >
                Upload a clear, recent photo of the caregiver for identification
                purposes. This photo will be used across the dashboard for quick
                visual identification.
              </p>
              {photoFile && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={GREEN}
                    strokeWidth="2"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <span style={{ fontSize: 13, color: GREEN, fontWeight: 600 }}>
                    {photoFile.name}
                  </span>
                  <span style={{ fontSize: 11, opacity: 0.5 }}>
                    ({(photoFile.size / 1024).toFixed(1)} KB)
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoFile(null);
                      setPhotoPreview(null);
                    }}
                    style={{
                      background: "rgba(255,107,107,0.2)",
                      border: "1px solid rgba(255,107,107,0.4)",
                      borderRadius: 6,
                      padding: "4px 10px",
                      fontSize: 11,
                      color: "#FF6B6B",
                      cursor: "pointer",
                      marginLeft: 8,
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Personal Information ── */}
          <div>
            <h3
              style={{
                margin: "0 0 16px",
                fontSize: 16,
                fontWeight: 700,
                color: ACCENT,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={ACCENT}
                strokeWidth="2"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
              </svg>
              Personal Information
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: gridCols,
                gap: 16,
              }}
            >
              <FormField label="First Name" required isMobile={isMobile}>
                <TextInput
                  value={firstName}
                  onChange={setFirstName}
                  placeholder="e.g. Sarah"
                />
              </FormField>
                            <FormField label="Last Name" required isMobile={isMobile}>
                <TextInput
                  value={lastName}
                  onChange={setLastName}
                  placeholder="e.g. Johnson"
                />
              </FormField>
              <FormField label="Date of Birth" required isMobile={isMobile}>
                <TextInput
                  value={dob}
                  onChange={setDob}
                  type="date"
                />
              </FormField>
              <FormField label="Email" required isMobile={isMobile}>
                <TextInput
                  value={email}
                  onChange={setEmail}
                  placeholder="e.g. sarah.johnson@facility.com"
                  type="email"
                />
              </FormField>
              <FormField label="Phone" isMobile={isMobile}>
                <TextInput
                  value={phone}
                  onChange={setPhone}
                  placeholder="e.g. +1-555-0123"
                  type="tel"
                />
              </FormField>
            </div>
          </div>

          {/* ── Professional Details ── */}
          <div>
            <h3
              style={{
                margin: "0 0 16px",
                fontSize: 16,
                fontWeight: 700,
                color: ACCENT,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={ACCENT}
                strokeWidth="2"
              >
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
              </svg>
              Professional Details
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: gridCols,
                gap: 16,
              }}
            >
              <FormField label="Role" required isMobile={isMobile}>
                <SelectInput
                  value={role}
                  onChange={setRole}
                  options={ROLE_OPTIONS}
                  placeholder="Select role"
                />
              </FormField>
              <FormField label="Primary Shift" required isMobile={isMobile}>
                <SelectInput
                  value={primaryShift}
                  onChange={setPrimaryShift}
                  options={SHIFT_OPTIONS}
                  placeholder="Select shift"
                />
              </FormField>
              <FormField label="Badge ID" isMobile={isMobile}>
                <TextInput
                  value={badgeId}
                  onChange={setBadgeId}
                  placeholder="Auto-generated if empty"
                />
              </FormField>
              <FormField label="Employee ID" isMobile={isMobile}>
                <TextInput
                  value={employeeId}
                  onChange={setEmployeeId}
                  placeholder="Auto-generated if empty"
                />
              </FormField>
              <FormField label="Max Resident Load" isMobile={isMobile}>
                <TextInput
                  value={maxResidentLoad}
                  onChange={setMaxResidentLoad}
                  placeholder="e.g. 10"
                  type="number"
                  min="1"
                  max="20"
                />
              </FormField>
              <FormField label="Status" isMobile={isMobile}>
                <SelectInput
                  value={status}
                  onChange={setStatus}
                  options={STATUS_OPTIONS}
                  placeholder="Select status"
                />
              </FormField>
            </div>
          </div>

          {/* ── Training & Security ── */}
          <div>
            <h3
              style={{
                margin: "0 0 16px",
                fontSize: 16,
                fontWeight: 700,
                color: ACCENT,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={ACCENT}
                strokeWidth="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Training & Security
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Fall Response Training toggle */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "pointer",
                  padding: "10px 16px",
                  background: fallResponseTrained
                    ? "rgba(106,221,0,0.1)"
                    : INPUT_BG,
                  border: `1.5px solid ${
                    fallResponseTrained
                      ? "rgba(106,221,0,0.4)"
                      : BORDER
                  }`,
                  borderRadius: 10,
                  transition: "all 0.2s",
                }}
              >
                <div
                  onClick={() =>
                    setFallResponseTrained(!fallResponseTrained)
                  }
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    background: fallResponseTrained ? GREEN : "rgba(255,255,255,0.2)",
                    position: "relative",
                    transition: "background 0.2s",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: W,
                      position: "absolute",
                      top: 3,
                      left: fallResponseTrained ? 23 : 3,
                      transition: "left 0.2s",
                    }}
                  />
                </div>
                <span style={{ fontSize: 14, fontWeight: 500, color: W }}>
                  Fall Response Trained
                </span>
              </label>

                            {/* MFA Enabled toggle */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "pointer",
                  padding: "10px 16px",
                  background: mfaEnabled
                    ? "rgba(106,221,0,0.1)"
                    : INPUT_BG,
                  border: `1.5px solid ${
                    mfaEnabled
                      ? "rgba(106,221,0,0.4)"
                      : BORDER
                  }`,
                  borderRadius: 10,
                  transition: "all 0.2s",
                }}
              >
                <div
                  onClick={() => setMfaEnabled(!mfaEnabled)}
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    background: mfaEnabled ? GREEN : "rgba(255,255,255,0.2)",
                    position: "relative",
                    transition: "background 0.2s",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: W,
                      position: "absolute",
                      top: 3,
                      left: mfaEnabled ? 23 : 3,
                      transition: "left 0.2s",
                    }}
                  />
                </div>
                <span style={{ fontSize: 14, fontWeight: 500, color: W }}>
                  Multi-Factor Authentication (MFA) Enabled
                </span>
              </label>

              {/* Available for Assignment toggle */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "pointer",
                  padding: "10px 16px",
                  background: isAvailable
                    ? "rgba(106,221,0,0.1)"
                    : INPUT_BG,
                  border: `1.5px solid ${
                    isAvailable
                      ? "rgba(106,221,0,0.4)"
                      : BORDER
                  }`,
                  borderRadius: 10,
                  transition: "all 0.2s",
                }}
              >
                <div
                  onClick={() => setIsAvailable(!isAvailable)}
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    background: isAvailable ? GREEN : "rgba(255,255,255,0.2)",
                    position: "relative",
                    transition: "background 0.2s",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: W,
                      position: "absolute",
                      top: 3,
                      left: isAvailable ? 23 : 3,
                      transition: "left 0.2s",
                    }}
                  />
                </div>
                <span style={{ fontSize: 14, fontWeight: 500, color: W }}>
                  Available for New Assignments
                </span>
              </label>
                        </div>
          </div>

          {/* ── Certifications Section ── */}
          <div>
            <h3
              style={{
                margin: "0 0 16px",
                fontSize: 16,
                fontWeight: 700,
                color: ACCENT,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={ACCENT}
                strokeWidth="2"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              Certifications & Licenses
            </h3>
            
            {/* Certifications List */}
            {certifications.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
                {certifications.map((cert, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: INPUT_BG,
                      border: `1.5px solid ${BORDER}`,
                      borderRadius: 10,
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: W, marginBottom: 4 }}>
                        {cert.cert_name || cert.cert_type}
                      </div>
                      <div style={{ fontSize: 12, color: W, opacity: 0.6 }}>
                        {cert.cert_number && `#${cert.cert_number} • `}
                        {cert.issuing_body && `${cert.issuing_body} • `}
                        Expires: {cert.expiry_date || "N/A"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = certifications.filter((_, i) => i !== idx);
                        setCertifications(updated);
                      }}
                      style={{
                        background: "rgba(255,107,107,0.2)",
                        border: "1px solid rgba(255,107,107,0.4)",
                        borderRadius: 8,
                        padding: "6px 12px",
                        fontSize: 12,
                        color: "#FF6B6B",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Certification Form */}
            {showAddCert ? (
              <div
                style={{
                  background: "rgba(74,144,226,0.1)",
                  border: `1.5px solid rgba(74,144,226,0.3)`,
                  borderRadius: 10,
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 12 }}>
                  <FormField label="Certification Type" required isMobile={isMobile}>
                    <SelectInput
                      value={newCert.cert_type}
                      onChange={(val) => setNewCert({ ...newCert, cert_type: val })}
                      options={CERT_TYPE_OPTIONS}
                      placeholder="Select type"
                    />
                  </FormField>
                  <FormField label="Certification Name" isMobile={isMobile}>
                    <TextInput
                      value={newCert.cert_name}
                      onChange={(val) => setNewCert({ ...newCert, cert_name: val })}
                      placeholder="e.g. Basic Life Support"
                    />
                  </FormField>
                  <FormField label="Certificate Number" isMobile={isMobile}>
                    <TextInput
                      value={newCert.cert_number}
                      onChange={(val) => setNewCert({ ...newCert, cert_number: val })}
                      placeholder="e.g. BLS-2024-12345"
                    />
                  </FormField>
                  <FormField label="Issuing Organization" isMobile={isMobile}>
                    <TextInput
                      value={newCert.issuing_body}
                      onChange={(val) => setNewCert({ ...newCert, issuing_body: val })}
                      placeholder="e.g. American Heart Association"
                    />
                  </FormField>
                  <FormField label="Issue Date" isMobile={isMobile}>
                    <TextInput
                      value={newCert.issued_date}
                      onChange={(val) => setNewCert({ ...newCert, issued_date: val })}
                      type="date"
                    />
                  </FormField>
                                    <FormField label="Expiry Date" required isMobile={isMobile}>
                    <TextInput
                      value={newCert.expiry_date}
                      onChange={(val) => setNewCert({ ...newCert, expiry_date: val })}
                      type="date"
                    />
                  </FormField>
                </div>
                <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddCert(false);
                      setNewCert({
                        cert_type: "",
                        cert_name: "",
                        cert_number: "",
                        issuing_body: "",
                        issued_date: "",
                        expiry_date: "",
                      });
                    }}
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      border: `1.5px solid ${BORDER}`,
                      borderRadius: 8,
                      padding: "8px 16px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: W,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                                    <button
                    type="button"
                                        onClick={() => {
                      // Validate required fields
                      if (!newCert.cert_type || !newCert.expiry_date) {
                        setError("Certification type and expiry date are required");
                        return;
                      }
                      
                      // Validate certification dates
                      const certValidation = validateCertificationDates(newCert.issued_date, newCert.expiry_date);
                      if (!certValidation.isValid) {
                        setError(certValidation.error);
                        return;
                      }
                      
                      setCertifications([...certifications, { ...newCert }]);
                      setShowAddCert(false);
                      setNewCert({
                        cert_type: "",
                        cert_name: "",
                        cert_number: "",
                        issuing_body: "",
                        issued_date: "",
                        expiry_date: "",
                      });
                    }}
                    style={{
                      background: GREEN,
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 16px",
                      fontSize: 13,
                      fontWeight: 700,
                      color: W,
                      cursor: "pointer",
                    }}
                  >
                    Add Certification
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowAddCert(true)}
                style={{
                  background: "rgba(74,144,226,0.15)",
                  border: `1.5px dashed rgba(74,144,226,0.4)`,
                  borderRadius: 10,
                  padding: "12px 16px",
                  fontSize: 14,
                  fontWeight: 600,
                  color: ACCENT,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  width: "100%",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(74,144,226,0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(74,144,226,0.15)";
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={ACCENT}
                  strokeWidth="2.5"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add Certification
              </button>
                        )}
          </div>

          {/* ═══ ERROR BANNER (at bottom) ═══ */}
          {error && (
            <div
              style={{
                background: "rgba(255,107,107,0.12)",
                border: "1.5px solid rgba(255,107,107,0.5)",
                borderRadius: 12,
                padding: "14px 20px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                animation: "fadeIn 0.3s ease-out",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FF6B6B"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <span style={{ fontSize: 14, color: "#FF6B6B", fontWeight: 500 }}>
                {error}
              </span>
              <button
                onClick={() => setError(null)}
                style={{
                  marginLeft: "auto",
                  background: "transparent",
                  border: "none",
                  color: "#FF6B6B",
                  cursor: "pointer",
                  fontSize: 18,
                }}
              >
                ✕
              </button>
            </div>
          )}

          {/* ── Submit Buttons ── */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 16,
              paddingTop: 16,
              borderTop: `1.5px solid ${BORDER}`,
            }}
          >
            <button
              type="button"
              onClick={onBack}
              disabled={submitting}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: `1.5px solid ${BORDER}`,
                borderRadius: 12,
                padding: "14px 32px",
                fontSize: 15,
                fontWeight: 600,
                color: W,
                cursor: submitting ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                opacity: submitting ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!submitting)
                  e.currentTarget.style.background = "rgba(255,255,255,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.08)";
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                background: submitting
                  ? "rgba(106,221,0,0.3)"
                  : "linear-gradient(135deg, #6ADD00, #4CAF50)",
                border: "none",
                borderRadius: 12,
                padding: "14px 40px",
                fontSize: 15,
                fontWeight: 700,
                color: submitting ? "rgba(255,255,255,0.6)" : W,
                cursor: submitting ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 10,
                boxShadow: submitting
                  ? "none"
                  : "0 4px 16px rgba(106,221,0,0.3)",
              }}
              onMouseEnter={(e) => {
                if (!submitting) e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {submitting ? (
                <>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={W}
                    strokeWidth="2"
                    style={{
                      animation: "spin 1s linear infinite",
                    }}
                  >
                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                  </svg>
                  Adding Caregiver...
                </>
              ) : (
                <>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={W}
                    strokeWidth="2.5"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Add Caregiver
                </>
              )}
            </button>
          </div>
        </div>
      )}

    </main>
  );
}
